import { Injectable } from '@angular/core'
import { State, Action, StateContext, Selector } from '@ngxs/store'
import { append, patch, removeItem } from '@ngxs/store/operators'
import { AppActions } from './app.actions'
import { ContactState } from './contact/contact.state'
import { QuoteState } from './quote/quote.state'
import { SettingsState } from './settings/settings.state'
import { UserState } from './user/user.state'

// prettier-ignore
export interface AppStateModel {
	loading: boolean;
  loadingUrls: string[];
  hasError: boolean;
  errorMessage: string;
  allowNewSession: boolean;
}

const defaults: AppStateModel = {
	loading: true,
	loadingUrls: [],
	hasError: false,
	errorMessage: '',
	allowNewSession: true,
}

@State<AppStateModel>({
	name: 'app',
	defaults,
	children: [UserState, SettingsState],
})
@Injectable()
export class AppState {
	@Action(AppActions.SetLoading)
	setLoading(
		{ getState, setState }: StateContext<AppStateModel>,
		{ payload }: AppActions.SetLoading
	) {
		const state = getState()
		setState({
			...state,
			loading: payload,
		})
	}

	@Action(AppActions.AddLoadingUrl)
	addLoadingUrl({ setState }: StateContext<AppStateModel>, { payload }: AppActions.AddLoadingUrl) {
		setState(
			patch<AppStateModel>({
				loadingUrls: append([payload]),
			})
		)
	}

	@Action(AppActions.RemoveLoadingUrl)
	removeLoadingUrl(
		{ setState }: StateContext<AppStateModel>,
		{ payload }: AppActions.RemoveLoadingUrl
	) {
		setState(
			patch<AppStateModel>({
				loadingUrls: removeItem((i) => i === payload),
			})
		)
	}

	@Action(AppActions.SetError)
	setError({ patchState }: StateContext<AppStateModel>, { payload }: AppActions.SetError) {
		patchState({
			hasError: true,
			errorMessage: payload,
		})
	}

	@Action(AppActions.ClearError)
	clearError({ patchState }: StateContext<AppStateModel>) {
		patchState({
			hasError: false,
			errorMessage: '',
		})
	}

	@Action(AppActions.AllowSessionCreation)
	allowSessionCreation({ patchState }: StateContext<AppStateModel>) {
		patchState({ allowNewSession: true })
	}

	@Action(AppActions.DisallowSessionCreation)
	disallowSessionCreation({ patchState }: StateContext<AppStateModel>) {
		patchState({ allowNewSession: false })
	}

	@Selector()
	static error(state: AppStateModel) {
		const { hasError, errorMessage } = state
		return {
			hasError,
			errorMessage,
		}
	}

	@Selector()
	static isLoading(state: AppStateModel) {
		return state.loading
	}

	@Selector()
	static isLoadingUrls(state: AppStateModel) {
		return state.loadingUrls.length > 0
	}

	@Selector()
	static allowNewSession(state: AppStateModel) {
		return state.allowNewSession
	}
}
